import Vue from 'vue';
import { CError } from './error';
import type { EntityOfType } from '@cognitoforms/model.js';
import type FormBase from 'src/components/FormBase';
import type { FormSession } from 'src/web-api/form-session';
import type { FormEntry } from '@cognitoforms/types/server-types/forms/model/form-entry';
import type AppInsights from './app-insights';

const ignoredExceptions = new Set([
	'ResizeObserver loop limit exceeded',
	'ResizeObserver loop completed with undelivered notifications.',
	'Script error.',
	'ErrorEvent: Script error.'
]);
const ignoreExceptionsStartingWith = new Array(
	'PromiseRejectionEvent: [object String]'
);

const FAUX_KEY = 'faux-key';

/**
 * Times a single operation. Each call to `ILogger.startEvent` returns its own timer, so
 * concurrent operations sharing an event name cannot interfere with one another.
 */
export interface EventTimer {
	stop(additionalProperties?: Record<string, unknown>, measurements?: Record<string, number>): void;
}

export interface ILogger {
	error(error: CError | Error, additionalProperties?: Record<string, unknown>) : void;
	warn(message: string, trace?: string, additionalProperties?: Record<string, unknown>): void;
	customEvent(eventName: string, additionalProperties?: Record<string, unknown>, measurements?: Record<string, number>): void;
	metric(metricName: string, amount: number, additionalProperties?: Record<string, unknown>): void;
	trace(message: string, additionalProperties?: Record<string, unknown>): void;
	startEvent(eventName: string): EventTimer;
}

export default class Log implements ILogger {
	constructor(session: FormSession) {
		this.session = session;
		if (!this.isEnabled)
			return;

		if (!Log.appInsights) {
			Log.appInsights = import(/* webpackChunkName: "app-insights" */ './app-insights').then(mod => {
				const AppInsightsCtor = mod.default;
				const appInsights = new AppInsightsCtor(FAUX_KEY, '/svc/log');

				appInsights.addTelemetryInitializer(Log.ignoreUselessErrors.bind(this));
				appInsights.addTelemetryInitializer(Log.filterSuccessfulDependencies.bind(this));

				appInsights.addTelemetryInitializer(function ignoreItemsWithRealKey(item) {
					// when the admin site's global app insights instance is available, sometimes we
					// get telemetry items with the real instrumentation key
					return item.iKey === FAUX_KEY;
				});

				// append metadata to telemetry
				appInsights.addTelemetryInitializer(item => {
					if (item.baseData) {
						item.baseData.properties = item.baseData.properties || {};
						Object.assign(item.baseData.properties, this.metadata);
					}
				});

				// This logic is repeated in CleanseTelemetryProcessor.cs and ai.js (and ai.min.js)
				appInsights.addTelemetryInitializer(function cleanseUrls(item) {
					if (item.baseType === 'PageviewData') {
						item.baseData.uri = Log.cleanseUrl(item.baseData.uri);
					}

					if (item.baseType === 'RemoteDependencyData') {
						item.baseData.name = Log.cleanseUrl(item.baseData.name);
						item.baseData.target = Log.cleanseUrl(item.baseData.target);
					}
				});
				return appInsights;
			});
		}

		// eslint-disable-line no-use-before-define
		Vue.config.errorHandler = Log.onError;
		Vue.config.warnHandler = Log.onWarning;
	}

	// -------------- Properties --------------
	private entry: EntityOfType<FormEntry>;
	private session: FormSession;
	private static appInsights: Promise<AppInsights>;

	// -------------- Computed --------------
	get isEnabled(): boolean {
		return this.session.flags.log;
	}

	get organizationId(): string {
		return this.session.organizationId;
	}

	get formName(): string {
		return this.entry && this.entry.Form ? this.entry.Form.InternalName : null;
	}

	get formId(): string {
		return this.session.formId;
	}

	get entryId(): string {
		return this.entry ? this.entry.Id : null;
	}

	// -------------- Methods --------------

	error(error: CError | Error, additionalProperties?: Record<string, unknown>) : void {
		const cError = error as CError;
		if (additionalProperties) {
			cError.additionalProperties = {
				...cError.additionalProperties,
				...additionalProperties
			};
		}

		Log.error(error as CError, this);
	}

	warn(message: string, trace?: string, additionalProperties?: Record<string, unknown>): void {
		Log.warn(message, this, trace, additionalProperties);
	}

	async pageLoad() {
		if (!this.isEnabled)
			return;

		(await Log.appInsights).pageView();
	}

	registerEntry(entry: EntityOfType<FormEntry>) {
		this.entry = entry;
	}

	customEvent(eventName: string, additionalProperties?: Record<string, unknown>, measurements?: Record<string, number>) {
		Log.customEvent(eventName, this, additionalProperties, measurements);
	}

	startEvent(eventName: string): EventTimer {
		const started = performance.now();
		let stopped = false;

		return {
			stop: (additionalProperties?: Record<string, unknown>, measurements?: Record<string, number>) => {
				if (stopped)
					return;

				stopped = true;
				const duration = Math.round(performance.now() - started).toString();
				this.customEvent(eventName, { ...additionalProperties, duration }, measurements);
			}
		};
	}

	metric(metricName: string, amount: number, additionalProperties?: Record<string, unknown>) {
		Log.metric(metricName, amount, this, additionalProperties);
	}

	trace(message: string, additionalProperties?: Record<string, unknown>) {
		Log.trace(message, this, additionalProperties);
	}

	private static async error(error: CError, log: Log) {
		console.error(error.message, error);

		// Some errors are only for the end-user. A more detailed error was probably already logged
		if (!log || !log.isEnabled || error.log === false)
			return;

		(await Log.appInsights).log(error, error.additionalProperties);

		// Prevent the error from being logged again by additional throws or subsequent calls to `logError`
		error.log = false;
	}

	private static async warn(message: string, log: Log, trace?: string, additionalProperties?: Record<string, unknown>) {
		if (!log || !log.isEnabled)
			return;

		const error = new CError(message);
		error.stack = trace;

		(await Log.appInsights).log(error, { isWarning: 'true', ...additionalProperties });

		console.warn(error, error.message);
	}

	private static async customEvent(eventName: string, log: Log, additionalProperties: Record<string, unknown>, measurements?: Record<string, number>) {
		if (!log || !log.isEnabled)
			return;

		(await Log.appInsights).logCustomEvent(eventName, additionalProperties, measurements);
	}

	private static async metric(metricName: string, amount: number, log: Log, additionalProperties: Record<string, unknown>) {
		if (!log || !log.isEnabled)
			return;

		(await Log.appInsights).logMetric(metricName, amount, additionalProperties);
	}

	private static async trace(message: string, log: Log, additionalProperties: Record<string, unknown>) {
		if (!log || !log.isEnabled)
			return;

		(await Log.appInsights).logTrace(message, additionalProperties);
	}

	private get metadata() {
		return {
			'OrganizationId': this.organizationId,
			'FormName': this.formName,
			'FormId': this.formId,
			'EntryId': this.entryId,
			'FormClient': true
		};
	}

	private static patterns = [
		/(\?|&)token(=[^&]*)?|^token(=[^&]*)?&?/ig, // Remove session tokens
		/[=#:](.{44})[*!]/ig, // Remove save & resume tokens
		/F-[!$0-9a-zA-Z]{22}/ig // Remove file ID references
	];

	private static blankOut = '*****';

	public static cleanseUrl(url: string) {
		if (!url.toLowerCase().includes('http') || url === '') {
			return url;
		}

		// Replace all sensitive information before reporting to AI
		for (let i = 0; i < this.patterns.length; i++) {
			url = url.replaceAll(this.patterns[i], this.blankOut);
		}

		return url;
	}

	public static ignoreUselessErrors(item: any): boolean {
		if (item.baseType !== 'ExceptionData')
			return true;

		const message: string = item.baseData?.exceptions?.[0]?.message ?? item.data?.message;
		if (typeof message !== 'string')
			return true;

		const lowerMessage = message.toLowerCase();
		return !(ignoredExceptions.has(message) ||
			ignoredExceptions.has(`ErrorEvent: ${message}`) ||
			ignoredExceptions.has(message.replace(/^ErrorEvent: /, '')) ||
			ignoreExceptionsStartingWith.some(ignoredMessage => lowerMessage.includes(ignoredMessage.toLowerCase())));
	}

	public static filterSuccessfulDependencies(item: any): boolean {
		if (item.baseType === 'RemoteDependencyData') {
			let data = item.baseData;

			if (!data)
				data = item.data;

			if (data) {
				if (data.responseCode && data.duration && data.responseCode.toString().startsWith('2') && data.duration < 10000)
					return false;

				// ignore GA requests (likely caused by blocker extensions)
				if (data.target && (data.target.includes('google-analytics.com') || data.target.includes('analytics.google.com')))
					return false;
			}
		}
		return true;
	}

	private static getInstance(obj: Vue | FormBase): Log {
		return obj ? (obj.$root as FormBase).log as Log : null;
	}

	private static onError(error: Error, $vm: Vue): void {
		if (ignoredExceptions.has(error.message))
			return;

		Log.error(error, Log.getInstance($vm));
	}

	private static onWarning(message: string, $vm: Vue, trace: string): void {
		Log.warn(message, Log.getInstance($vm), trace);
	}
}